import { useEffect, useRef, useState } from "react";

const DEFAULT_PAGE_SIZES = [10, 20, 30, 50];

/**
 * Server-paged list state for the results screens (Auction Results, Secondary Market Results).
 *
 * All six role copies of those screens carried the same hand-rolled paging, and with it the same
 * race: changing the page size fetched the CURRENT page at the new size and then reset to page 1,
 * whose effect fetched again. Off page 1 the two requests raced, and whichever answered last won,
 * so the table could show page N's rows under a pager reading "1 of X". Here one effect owns every
 * fetch, keyed on both page and page size, and a response that a newer request has superseded is
 * dropped rather than written over the newer one.
 *
 * @param {(query: {PageNumber: number, PageSize: number, SortOrder: boolean}) => Promise<any>} fetchPage
 *   An auth-service wrapper, e.g. SecondaryMarketAnnouncement. Pass the imported function itself:
 *   it is an effect dependency, so a new function on every render would refetch on every render.
 * @param {number[]} [pageSizes] The rows-per-page options; the first is the initial size.
 * @returns {{rows: any[], page: number, perPage: number, totalPages: number, totalRecords: number,
 *   loading: boolean, failed: boolean, pageSizes: number[], nextPage: () => void,
 *   previousPage: () => void, changePerPage: (size: number) => void, reload: () => void,
 *   updateRows: (next: any[] | ((rows: any[]) => any[])) => void}}
 *
 * #133 (the player's Managers page) added three things the results screens do not use:
 * - a fetchPage that closes over a filter (a tab, a search) is passed as a NEW function when the
 *   filter changes; that goes back to page 1, with one request for page 1 rather than one for the
 *   old page first;
 * - reload() asks for the current page again, after the rows on it have changed (a request sent,
 *   a request cancelled);
 * - totalRecords, the server's totalRecordCount, for a "Showing 1–6 of 12" line;
 * - loading, true while the latest request is unanswered, so an empty state is not shown before
 *   the first page has arrived.
 *
 * The Broadcasting Center (#167 / #168) added failed: true when the latest request got no answer -
 * no reply, or a 5xx, whose ErrorDetails body or gateway page holds no rows. The rows already on
 * screen are kept, and the screen can say they are not current instead of showing an empty state.
 * The next answered request clears it.
 *
 * FE-16 (#4, the Manager's Auctions list) added updateRows: it changes the rows on screen without
 * a request - a hub push, a pause - as a useState setter does, and the next page that arrives
 * replaces them.
 */
export const usePagedList = (fetchPage, pageSizes = DEFAULT_PAGE_SIZES) => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(pageSizes[0]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const lastFetchPageRef = useRef(fetchPage);

  useEffect(() => {
    // A new fetchPage is a new filter: start again from page 1. Off page 1 the request is left to
    // the render that page change causes, so the old page of the new filter is never fetched.
    if (lastFetchPageRef.current !== fetchPage) {
      lastFetchPageRef.current = fetchPage;
      if (page !== 1) {
        setPage(1);
        return undefined;
      }
    }

    let current = true;
    setLoading(true);

    fetchPage({ PageNumber: page, PageSize: perPage, SortOrder: false }).then((response) => {
      // Wrappers never throw: no reply comes back as undefined and a server failure as its 5xx
      // response (apiClient has already toasted both), and the rows on screen are kept rather than
      // blanked.
      if (!current) return;
      setLoading(false);
      const unanswered = !response || response.status >= 500;
      setFailed(unanswered);
      if (unanswered || !response.data) return;
      setRows(response.data.data?.dataList || []);
      setTotalPages(response.data.data?.totalPageSize || 0);
      setTotalRecords(response.data.data?.totalRecordCount || 0);
    });

    return () => {
      current = false;
    };
  }, [fetchPage, page, perPage, reloadKey]);

  const nextPage = () => setPage((p) => (p < totalPages ? p + 1 : p));
  const previousPage = () => setPage((p) => (p > 1 ? p - 1 : p));

  // Both updates land in one render (React 18 batches them), so this is one fetch, for page 1.
  const changePerPage = (size) => {
    setPerPage(size);
    setPage(1);
  };

  const reload = () => setReloadKey((k) => k + 1);

  return {
    rows,
    page,
    perPage,
    totalPages,
    totalRecords,
    loading,
    failed,
    pageSizes,
    nextPage,
    previousPage,
    changePerPage,
    reload,
    updateRows: setRows,
  };
};
